import { Component, ElementRef, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { TdoNoteFilterModel } from '../models/tdo-note-filter.model';
import { EnumValidValueItemModel } from '../../../../models/enum/enum-valid-value-item.model';
import { NoteDataModel } from '../../../../models/notes/note-data.model';
import { Constants } from '../../../../constants';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TdoNoteBuilderComponent } from '../../tdo-note-builder/tdo-note-builder.component';
import { TdoMacroNotesComponent } from '../../tdo-macro-notes/tdo-macro-notes.component';
import { NotesService } from '../../../../services/notes.service';
import { TdoNoteAssociatedObject, TdoNoteSaveModel } from '../models/tdo-note-save-model';
import { TdoNoteQueryModel } from '../models/tdo-note-query.model';
import { Globals } from '../../../../globals';
import { NoteCategoryDataModel } from '../../../../models/notes/note-category-data.model';
import { NoteTypeDataModel } from '../../../../models/notes/note-type-data.model';
import { StaticDataService } from '../../../../services/static-data.service';
import { AuthenticationService } from '../../../../modules/auth/services/authentication.service';
import { UserRoleEnum } from '../../../../models/enum/user-role.enum';
import { NoteDialogComponent } from "./dialog/note-dialog/note-dialog.component";
import { NoteTypeEnum } from '../../../../models/enum/note-type.enum';
import { DialogService } from 'src/app/services/dialog.service';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {  NgZone, ViewChild} from '@angular/core';
import {take} from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-tdo-note',
  templateUrl: './tdo-note.component.html',
  styleUrls: ['./tdo-note.component.scss']
})
export class TdoNoteComponent implements OnInit, OnChanges {
  private static readonly TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000; // 86400000

  @Input() storedNoteFilterSettings: TdoNoteFilterModel;
  @Input() note: NoteDataModel;
  @Input() notesCategories: EnumValidValueItemModel[] = [];
  @Input() notesTypes: EnumValidValueItemModel[] = [];
  @Input() autoSaveMsDelay: number = 2000;
  @Input() isLastItem: boolean = false;

  @Input() noteQueryModel: TdoNoteQueryModel;
  @Output() noteChange: EventEmitter<NoteDataModel> = new EventEmitter<NoteDataModel>();


  isNoteSaving: boolean = false;
  saveTimeout: any;
  isReadOnly: boolean = false;

  // Tracks the user's latest type/category selections until the server confirms
  // them. Protected against getNotes() refreshes that return stale data.
  private _pendingTypes: NoteTypeDataModel[] | null = null;
  private _pendingCategories: NoteCategoryDataModel[] | null = null;

  @ViewChild('autosize') autosize: CdkTextareaAutosize;
  @ViewChild('noteArea') noteArea!: ElementRef<HTMLTextAreaElement>;

  constructor(private _ngZone: NgZone,
    private dialog: MatDialog,
    private staticDataService: StaticDataService,
    private globals: Globals,
    private authenticationService: AuthenticationService,
    private dialogService: DialogService,
    private notesService: NotesService) { }

  ngOnInit(): void {
    this.updateReadOnlyState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['note'] && changes['note'].currentValue) {
      if (!changes['note'].firstChange) {
        const incoming = changes['note'].currentValue as NoteDataModel;

        if (this._pendingTypes !== null) {
          const incomingIds = new Set((incoming.dataNoteTypes || []).map(t => t.enumNoteTypeID));
          const pendingIds = new Set(this._pendingTypes.map(t => t.enumNoteTypeID));
          const serverMatchesPending =
            pendingIds.size === incomingIds.size &&
            [...pendingIds].every(id => incomingIds.has(id));
          if (serverMatchesPending) {
            this._pendingTypes = null;
          } else {
            this.note.dataNoteTypes = this._pendingTypes;
          }
        }

        if (this._pendingCategories !== null) {
          const incomingIds = new Set((incoming.dataNoteCategories || []).map(c => c.enumNoteCategoryID));
          const pendingIds = new Set(this._pendingCategories.map(c => c.enumNoteCategoryID));
          const serverMatchesPending =
            pendingIds.size === incomingIds.size &&
            [...pendingIds].every(id => incomingIds.has(id));
          if (serverMatchesPending) {
            this._pendingCategories = null;
          } else {
            this.note.dataNoteCategories = this._pendingCategories;
          }
        }
      }
      this.updateReadOnlyState();
    }
  }

  updateReadOnlyState(): void {
    if (!this.note) {
      this.isReadOnly = false;
      return;
    }
    
    const isLocked = this.isNoteLocked(this.note);
    const hasRoleToEdit = this.isUserHasRoleToEdit();
    this.isReadOnly = isLocked || !hasRoleToEdit;

  }
  triggerResize() {
    // Wait for changes to be applied, then trigger textarea resize.
    this._ngZone.onStable.pipe(take(1)).subscribe(() => this.autosize.resizeToFitContent(true));
  }

  getTextColor(note: NoteDataModel) {
    if (note && note.dataNoteTypes && note.dataNoteTypes.length > 0) {
      const alert = note.dataNoteTypes.find(p => p.enumNoteType.ItemDisplayValue.toLowerCase() === 'alert');
      if (alert) {
        return 'white';
      } else {
        return 'black';
      }
    } else {
      return '';
    }
  }

  getBackgroundColor(note: NoteDataModel) {
    if (note && note.dataNoteTypes && note.dataNoteTypes.length > 0) {
      const alert = note.dataNoteTypes.find(p => p.enumNoteType?.ItemDisplayValue?.toLowerCase() === 'alert');
      if (alert) {
        return 'rgba(255,0,0,1)';
      }
    }
    return '';
  }

  auto_grow(element) {
    console.log('input element ', element);

    element.style.height = "5px";
    element.style.height = (element.scrollHeight) + "px";
  }

  isNoteLocked(dataNote: NoteDataModel) {
    if (!dataNote) {
      return null;
    }

    // Check if explicitly locked
    if (dataNote.isLocked === true) {
        return true;
    }
    
    // Check if modified more than 24 hours ago
    const nowDate = new Date();
    const modifiedDate = new Date(dataNote.xauditModifiedOn);
    const msDiff = nowDate.getTime() - modifiedDate.getTime();

    // Num milliseconds in 24 hours
    return msDiff >= TdoNoteComponent.TWENTY_FOUR_HOURS_MS;
  }

  isUnlockDisabled(dataNote: NoteDataModel): boolean {
    if (!dataNote || !dataNote.isLocked) {
      return false;
    }

    // Check if note was created more than 24 hours ago
    const nowDate = new Date();
    const createdDate = new Date(dataNote.xauditCreatedOn);
    
    // Validate date - if invalid, allow unlock (fail safe)
    if (isNaN(createdDate.getTime())) {
      return false;
    }
    
    const msDiff = nowDate.getTime() - createdDate.getTime();
    return msDiff > TdoNoteComponent.TWENTY_FOUR_HOURS_MS;
  }

  getDate(date: any) {
    if (date != null) {
      if (date instanceof Date) {

      } else {
        if (!date.includes('Z')) {
          date += 'Z';
        }
      }
      const date1 = new Date(date);
      let hours = date1.getHours();
      let demarcation = 'AM';
      if (hours > 12) {
        hours -= 12;
        demarcation = 'PM';
      } else {
        if (hours === 0) {
          hours = 12;
        }
      }

      let minutes = '';
      const minutesNum = date1.getMinutes();
      if (minutesNum < 10) {
        minutes = '0' + minutesNum.toString();
      } else {
        minutes = minutesNum.toString();
      }
      return Constants.monthNamesAbbreviated[date1.getMonth()] + ' ' + date1.getDate() + ', ' + date1.getFullYear() + ' | ' + hours + ':' + minutes + ' ' + demarcation;
    }
  }

  onNoteInput(event: string, noteData: NoteDataModel) {
    if (this.isReadOnly) {
      return;
    }
    this.saveDataNote();
  }

  onNoteBlur(event: FocusEvent, noteData: NoteDataModel) {
    if (this.isReadOnly) {
      return;
    }
    this.saveDataNote();
  }

  onKeyDown(event: KeyboardEvent) {
    if (this.isReadOnly) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  onCategoryDropdownSelectionComplete(items: NoteCategoryDataModel[], noteData: NoteDataModel) {
    this.note.dataNoteCategories = items;
    this._pendingCategories = items;
    this.saveDataNote();
  }

  onTypeDropdownSelectionComplete(items: NoteTypeDataModel[], noteData: NoteDataModel) {
    this.note.dataNoteTypes = items;
    this._pendingTypes = items;
    // Always emit so the parent can add/remove the note from the alertNotes section
    // when the Alert type is toggled in either direction.
    this.noteChange.emit(this.note);
    this.saveDataNote();
  }

  deleteNote(): void {
    this.dialogService.openConfirmDialog(`Are you sure you want to delete this note?`).subscribe((result) => {
      if (result === true) {
        this.note.xauditDeleted = true;


        this.saveDataNote();
        this.noteChange.emit(this.note);
      }
    });
  }
  saveDataNote() {
    if (this.isNoteSaving) {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.saveDataNote();
      }, 1000);
      return;
    }
    this.isNoteSaving = true;
    const model = new TdoNoteSaveModel();
    model.text = this.note.Note;
    model.dataNote = this.note;


    if (model.dataNote.dataNoteCategories) {
      model.categoryEnumIds = model.dataNote.dataNoteCategories.map(p => p.enumNoteCategoryID);
    }

    if (model.dataNote.dataNoteTypes) {
      model.typeEnumIds = model.dataNote.dataNoteTypes.map(p => p.enumNoteTypeID);
    }

    const assoc = new TdoNoteAssociatedObject();
    assoc.parentObjectName = this.noteQueryModel.parentObjectName;
    assoc.objectId = this.noteQueryModel.objectId;
    model.patientId = this.noteQueryModel.patientId;

    const alreadyExistModel = model.associatedObjects.find(p => p.parentObjectName === this.noteQueryModel.parentObjectName && p.objectId === this.noteQueryModel.objectId);
    if (!alreadyExistModel) {
      model.associatedObjects.push(assoc);
    }
    this.notesService.saveNote(model).subscribe((res) => {
      // Patch only server-assigned fields onto the existing note object.
      // Do NOT replace this.note with the response: replacing the reference
      // disconnects the child from the parent's filteredNotes array, breaking
      // indexOf-based removal (delete) and causing race conditions where an
      // in-flight save response overwrites the user's pending type/category
      // changes (e.g. unchecking Alert while a save is already in progress).
      if (!this.note.NoteId && res.NoteId) {
        this.note.NoteId = res.NoteId;
      }
      this.note.xauditTableRowID = res.xauditTableRowID;
      this.note.xauditCreatedOn = res.xauditCreatedOn;
      this.note.xauditCreatedBy = res.xauditCreatedBy;
      this.note.xauditCreatedByUser = res.xauditCreatedByUser;
      this.note.xauditModifiedOn = res.xauditModifiedOn;
      this.note.xauditModifiedBy = res.xauditModifiedBy;
      this.note.xauditModifiedByUser = res.xauditModifiedByUser;
      this.isNoteSaving = false;
    },
    (error) => {
      console.error('saveDataNote error', error);
      this.isNoteSaving = false;
    }
  );
  }

  openNoteBuilder(dataNote: NoteDataModel) {
    const config = new MatDialogConfig();
    config.data = dataNote;
    config.panelClass = 'tdo-note-dialog-main';
    this.dialog.open(TdoNoteBuilderComponent, config).afterClosed().subscribe((data) => {
      if (data?.length > 0) {
        this.note.Note =  data;
        this.saveDataNote();
      }
    });
  }

  openMacroNotes(dataNote: NoteDataModel) {
    const config = new MatDialogConfig();
    config.data = dataNote;
    config.panelClass = 'tdo-note-dialog-main';
    const cursorPosition = this.noteArea.nativeElement.selectionStart;
    this.dialog.open(TdoMacroNotesComponent, config).afterClosed().subscribe((data) => {
      if(data?.length > 0) {
        this.note.Note = this.note.Note.substring(0, cursorPosition) + '\n' + data + ' ' + this.note.Note.substring(cursorPosition);
        //this.note.Note += '\n' + data;
        this.saveDataNote();
      }    
    });
  }

  isUserHasRoleToEdit() {
    return this.authenticationService.currentUserRolesIds.includes(UserRoleEnum.Notes);
  }

  openNoteDialog() {
    const config = new MatDialogConfig();
    config.panelClass = 'dialog';
    config.data = {
      note: this.note,
      isLocked: this.isNoteLocked(this.note)
    };
    this.dialog.open(NoteDialogComponent, config).afterClosed().subscribe((res) => {
      if (res) {
        this.note = res;
        this.saveDataNote();
      }
    });
  }

  lockNote() {
    this.dialogService.openConfirmDialog(`Are you sure you want to lock this note?`).subscribe((result) => {
      if (result === true) {
        this.note.xauditModifiedOn = new Date();
        this.note.xauditModifiedBy = this.authenticationService.currentUserValue.authdata.userid;
        this.note.isLocked = true;
        this.updateReadOnlyState();

        this.saveDataNote();
        this.noteChange.emit(this.note);
      }
    });
  }

  unlockNote() {
    // Check if unlock is disabled due to 24-hour rule
    if (this.isUnlockDisabled(this.note)) {
      this.dialogService.openConfirmDialog(`This note cannot be unlocked because it was created more than 24 hours ago.`, false).subscribe();
      return;
    }

    this.dialogService.openConfirmDialog(`Are you sure you want to unlock this note?`).subscribe((result) => {
      if (result === true) {
        this.note.xauditModifiedOn = new Date();
        this.note.xauditModifiedBy = this.authenticationService.currentUserValue.authdata.userid;
        this.note.isLocked = false;
        this.updateReadOnlyState();

        this.saveDataNote();
        this.noteChange.emit(this.note);
      }
    });
  }
}
